import { uuidv7 } from 'uuidv7'

import type { QueueSnapshot, QueueStorage, QueuedWrite, Transport, WriteStatus } from './types.js'

/**
 * The durable client-side write queue. §6.22 `OFF-001`, `OFF-001a`, `OFF-005`; §9.4 `ARC-006`.
 *
 * `OFF-001` `MUST`: "Every mutating action goes through a durable client-side write queue that
 * survives a dropped connection, a page reload and a browser crash. A sale in progress is never
 * lost to a thirty-second outage, which is the overwhelming majority of real failures."
 *
 * Release gate 7 is measured on this: "A sale completed during a 60-second network outage is
 * present and correct after reconnect, with no duplicate."
 *
 * ---
 *
 * **Order is preserved and is not optional.** Writes drain strictly in the order they were
 * enqueued, and one that cannot be sent stops the ones behind it rather than being skipped. A
 * payment that lands before the sale it pays for is not a race worth having, and the ordering is
 * cheaper to guarantee here than to repair in the ledger.
 *
 * **A refusal is never dropped.** `blocked` holds it for a human, because the alternative to a
 * visible failure is a sale nobody knows did not happen.
 */

/** Backoff in milliseconds by attempt count, then the last value repeats. */
const BACKOFF_MS = [0, 1_000, 3_000, 10_000, 30_000, 60_000] as const

export interface WriteQueueOptions {
  readonly storage: QueueStorage
  readonly transport: Transport
  /**
   * Injected rather than read from `Date.now()`, so a test can prove the backoff schedule instead
   * of waiting through it.
   */
  readonly now: () => number
  /**
   * `decisions/0005`: an idempotency key is retained 7 days. A `sent` write is kept that long so a
   * replayed request is still recognised, then dropped. Required, with no default, because the
   * number is a decision and a default would hide it.
   */
  readonly sentRetentionMs: number
  readonly onChange?: (snapshot: QueueSnapshot) => void
}

export interface EnqueueRequest {
  readonly method: QueuedWrite['method']
  readonly path: string
  readonly body: unknown
  readonly label: string
}

export class WriteQueue {
  readonly #options: WriteQueueOptions
  #writes: QueuedWrite[] = []
  #online = true
  #draining = false
  #loaded = false

  constructor(options: WriteQueueOptions) {
    this.#options = options
  }

  /** Read what survived the reload. `OFF-001`'s "survives a page reload and a browser crash". */
  async load(): Promise<void> {
    const stored = await this.#options.storage.all()
    // A write that was in flight when the tab died is `sending` in storage and was never confirmed.
    // It goes back to `pending` rather than being assumed sent: its idempotency key is unchanged,
    // so re-sending it is safe, and assuming success would lose a sale.
    this.#writes = stored
      .map((w) => (w.status === 'sending' ? { ...w, status: 'pending' as WriteStatus } : w))
      .sort((a, b) => a.enqueuedAt - b.enqueuedAt)
    this.#loaded = true
    await this.#persistAll()
    this.#emit()
  }

  get loaded(): boolean {
    return this.#loaded
  }

  /**
   * Put a write on the queue. This is the only way a component causes a mutation.
   *
   * The idempotency key is minted here, once. Every retry of this write carries it unchanged, which
   * is the entire point of `ARC-006`: the retry after a timeout is precisely the attempt that must
   * not be counted twice, and a key generated per attempt would defeat the header it satisfies.
   */
  async enqueue(request: EnqueueRequest): Promise<QueuedWrite> {
    const write: QueuedWrite = {
      id: uuidv7(),
      idempotencyKey: uuidv7(),
      method: request.method,
      path: request.path,
      body: request.body,
      status: 'pending',
      attempts: 0,
      enqueuedAt: this.#options.now(),
      label: request.label,
    }
    // Stored first, then registered. The other order left a write that failed to persist sitting in
    // memory and sent by the next drain, with the rejection travelling out of `enqueue` to whichever
    // screen called it: the request went out and was billed while the screen showed nothing,
    // because the line after the `await` never ran. `OFF-001` promises a durable queue, so a write
    // that is not durable is not on the queue, and the caller is told by the rejection.
    //
    // Reachable now in a way it was not: `IndexedDbQueueStorage.put` rejects on
    // `QuotaExceededError`, and a Mira ask carries up to four base64 photographs rather than a
    // sale's few hundred bytes.
    await this.#options.storage.put(write)
    this.#writes.push(write)
    this.#emit()
    return write
  }

  setOnline(online: boolean): void {
    this.#online = online
    this.#emit()
  }

  snapshot(): QueueSnapshot {
    const count = (status: WriteStatus): number =>
      this.#writes.filter((w) => w.status === status).length
    return {
      pending: count('pending'),
      sending: count('sending'),
      blocked: count('blocked'),
      online: this.#online,
      writes: [...this.#writes],
    }
  }

  /**
   * Send what can be sent, in order, and stop at the first thing that cannot.
   *
   * Returns when the queue is drained or the head is stuck. Safe to call repeatedly, and re-entrant
   * calls are ignored rather than queued: two concurrent drains would send the head twice, which
   * the idempotency key would make harmless and still nobody should write on purpose.
   */
  async drain(): Promise<void> {
    if (this.#draining || !this.#online) return
    this.#draining = true
    try {
      for (;;) {
        const next = this.#nextSendable()
        if (!next) break
        const sent = await this.#attempt(next)
        if (!sent) break
      }
      await this.#expireSent()
    } finally {
      this.#draining = false
      this.#emit()
    }
  }

  /**
   * A human clearing a blocked write, having dealt with it.
   *
   * Not automatic and not silent: a refusal that disappears on its own is a sale the shop believes
   * happened.
   */
  async discard(id: string): Promise<void> {
    this.#writes = this.#writes.filter((w) => w.id !== id)
    await this.#options.storage.remove(id)
    this.#emit()
  }

  /**
   * A human sending a blocked write again, having fixed whatever refused it.
   *
   * This exists because without it a single refusal stops the shop. `#nextSendable` returns nothing
   * on a blocked head, deliberately, so that a payment cannot overtake the sale it belongs to. The
   * consequence is that one refused prescription queues every later write behind it forever, and
   * before this the only exit was clearing the browser's storage.
   *
   * The attempt count resets, because the backoff measures a server that was unreachable and this is
   * a person saying the reason is gone. Not automatic: a refusal that retried itself is how a
   * validation failure becomes an infinite loop.
   */
  async retry(id: string): Promise<void> {
    const write = this.#writes.find((w) => w.id === id)
    if (!write || write.status !== 'blocked') return
    const cleared: QueuedWrite = { ...write, status: 'pending', attempts: 0 }
    delete (cleared as { error?: unknown }).error
    delete (cleared as { lastAttemptAt?: unknown }).lastAttemptAt
    const index = this.#writes.findIndex((w) => w.id === id)
    this.#writes[index] = cleared
    await this.#options.storage.put(cleared)
    this.#emit()
  }

  /**
   * A human keeping their own change, against the record as it now stands. `DESIGN-TOKENS.md` §16.5.
   *
   * `retry` is the wrong verb for a version conflict: the queued body carries the `expectedVersion`
   * that already lost the race, so re-sending it gets the same 409 for ever. This replaces that one
   * field with the version the server reported and sends it again, which is what "keep mine" means.
   *
   * **A new idempotency key, which is the part that is easy to get wrong.** Every other path in this
   * class keeps the key unchanged, because `ARC-006` is about the same request not being counted
   * twice. This is not the same request: the body differs, and `withIdempotency` hashes the body and
   * answers `IDEMPOTENCY_KEY_REUSED` to the same key carrying a different one. Reusing the key here
   * would turn a resolvable conflict into a permanent refusal.
   */
  async resolve(id: string, currentVersion: number): Promise<void> {
    const write = this.#writes.find((w) => w.id === id)
    if (!write || write.status !== 'blocked') return
    if (typeof write.body !== 'object' || write.body === null || Array.isArray(write.body)) return

    const rebased: QueuedWrite = {
      ...write,
      idempotencyKey: uuidv7(),
      body: { ...(write.body as Record<string, unknown>), expectedVersion: currentVersion },
      status: 'pending',
      attempts: 0,
    }
    delete (rebased as { error?: unknown }).error
    delete (rebased as { lastAttemptAt?: unknown }).lastAttemptAt

    const index = this.#writes.findIndex((w) => w.id === id)
    this.#writes[index] = rebased
    await this.#options.storage.put(rebased)
    this.#emit()
  }

  #nextSendable(): QueuedWrite | undefined {
    for (const write of this.#writes) {
      if (write.status === 'sent') continue
      // Order is strict: a blocked write holds the ones behind it. Skipping past it would let a
      // payment land before the sale it belongs to.
      if (write.status === 'blocked') return undefined
      if (write.status === 'pending' || write.status === 'sending') {
        const wait = BACKOFF_MS[Math.min(write.attempts, BACKOFF_MS.length - 1)] ?? 0
        const since = this.#options.now() - (write.lastAttemptAt ?? 0)
        if (write.attempts > 0 && since < wait) return undefined
        return write
      }
    }
    return undefined
  }

  async #attempt(write: QueuedWrite): Promise<boolean> {
    await this.#update(write.id, {
      status: 'sending',
      attempts: write.attempts + 1,
      lastAttemptAt: this.#options.now(),
    })
    const current = this.#writes.find((w) => w.id === write.id)
    if (!current) return false

    const outcome = await this.#options.transport(current)

    if (outcome.kind === 'accepted') {
      // The answer is stored with the write, so the screen that made it can read it back out of
      // the snapshot. Without this the queue is write-only and no screen can show its own result.
      await this.#update(write.id, { status: 'sent', response: outcome.body })
      return true
    }
    if (outcome.kind === 'refused') {
      // A validation failure, a permission denial or a 409 version conflict. Retrying changes
      // nothing, so it stops here and waits for a person.
      await this.#update(write.id, {
        status: 'blocked',
        error: {
          code: outcome.code,
          message: outcome.message,
          // The current state of the record, on a version conflict. Stored with the write because
          // the write outlives the response: it survives a reload, and so must the comparison a
          // shopkeeper is being asked to make.
          ...(outcome.details === undefined ? {} : { details: outcome.details }),
        },
      })
      return false
    }
    // Unreachable. Back to pending, and the backoff decides when it is tried again.
    await this.#update(write.id, { status: 'pending' })
    return false
  }

  async #update(id: string, patch: Partial<QueuedWrite>): Promise<void> {
    const index = this.#writes.findIndex((w) => w.id === id)
    if (index === -1) return
    const next = { ...(this.#writes[index] as QueuedWrite), ...patch } as QueuedWrite
    this.#writes[index] = next
    await this.#options.storage.put(next)
    this.#emit()
  }

  /** `decisions/0005`: a sent write is kept for the idempotency retention window, then dropped. */
  async #expireSent(): Promise<void> {
    const cutoff = this.#options.now() - this.#options.sentRetentionMs
    const expired = this.#writes.filter((w) => w.status === 'sent' && w.enqueuedAt < cutoff)
    for (const write of expired) {
      this.#writes = this.#writes.filter((w) => w.id !== write.id)
      await this.#options.storage.remove(write.id)
    }
  }

  async #persistAll(): Promise<void> {
    for (const write of this.#writes) await this.#options.storage.put(write)
  }

  #emit(): void {
    this.#options.onChange?.(this.snapshot())
  }
}
